

import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-aria-components';
import FilterTextSearchWrapper from '../Filters/FilterTextSearchWrapper';
import { useFilters } from '../../../hooks/UseFilters';
import { FilterTypes } from '../../../enums/FilterTypes';
import { FilterMatchType } from '../../../enums/FilterMatchType';
import { PropertyTypes } from '../../../enums/PropertyTypes';
import { ISchemaFilter } from '../entityComponents/Schema';
import FilterSelectWrapper from '../Filters/FilterSelectWrapper';
import CaseMaterialTypesDropdown from './CaseMaterialTypesDropdown';
import { staticSidePanelSignal } from '../layout/Layout';
import { EntityNames } from '../../../enums/EntityNames';
import { httpGet, httpPost } from '../../../modules/Backend';
import { TableVirtuoso } from 'react-virtuoso';
import IconSearch from "../../../assets/icons/icon-search-grey.svg"
import { PageSizes } from '../../../enums/PageSizes';
import Pill from '../Pill';
import { modalSignal } from '../Modal/ModalContainer';
import AddToRecallModal from '../Modal/AddToRecallModal';
import { Intent, IRecallData, IPartCase, IRecallItem } from './recallData';
import { HttpStatusCode } from 'axios';
import ConfirmModal, { ConfirmType } from '../Modal/ConfirmModal';
import { pascalToReadable } from '../../../helpers/StringHelper';
import Step2ListButton from './Step2ListButtons';
import SidePanelRecallInventory from './SidePanelRecallInventory';
import { ResultType } from '../../../enums/ResultTypes';
import LoadingButton from "../LoadingButton";
import { getAssumedItemReference, IParsedCode } from './parsedCode';
import { IOption } from '../../AdobeAriaLib/AdobeComboBox';
import NotesTooltip from './NotesTooltip';


interface Step2Props {
  displayName: string
  nextStep?: () => void;
  prevStep?: () => void;
  handleChange?: (input: string) => (value: any) => void;
  values?: Record<string, any>;
  errors?: Record<string, string>;
}

const filterData = {
  prefix: {
    PropertyName: "Prefix",
    entityName: EntityNames.prefixes,
    displayName: "Prefix",
    type: FilterTypes.select,
    required: false,
    className: "small-4",
    matchType: FilterMatchType.Equal,
    propertyType: PropertyTypes.string
  } as ISchemaFilter,
  caseNumber: {
    PropertyName: "Code",
    displayName: "Case Number",
    type: FilterTypes.search,
    required: true,
    className: "small-4",
    matchType: FilterMatchType.Equal,
    propertyType: PropertyTypes.code
  } as ISchemaFilter,
  type: {
    PropertyName: "CaseMaterialTypeId",
    entityName: EntityNames.caseMaterialTypes,
    displayName: "Types",
    type: FilterTypes.select,
    required: true,
    className: "small-4",
    matchType: FilterMatchType.Contains,
    propertyType: PropertyTypes.guidList
  } as ISchemaFilter,
  year: {
    PropertyName: "Year",
    displayName: "Year",
    type: FilterTypes.select,
    required: true,
    className: "small-4",
    matchType: FilterMatchType.Equal,
    propertyType: PropertyTypes.year
  } as ISchemaFilter,
}

const RecallStep2: React.FC<Step2Props> & { validate?: (values: Record<string, any>) => Record<string, string> } = ({
  displayName,
  nextStep,
  prevStep,
  handleChange,
  values,
  errors,
}) => {
  const {
    onFiltersChange, filters, clearFilters, setFilters
  } = useFilters({ schemaFilters: [] });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const pagesize = PageSizes.large;
  const [data, setData] = useState<any[]>()
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [SubmittedCode, setSubmittedCode] = useState<string | undefined | null>(undefined)
  const [SubmittedYear, setSubmittedYear] = useState<string | undefined | null>(undefined)
  const [SubmittedParsedCode, setSubmittedParsedCode] = useState<IParsedCode | undefined | null>(undefined)
  const [materialTypes, setMaterialTypes] = useState<IOption[]>([]);
  const [prefixes, setPrefixes] = useState<IOption[]>([]);
  const [years, setYears] = useState<IOption[]>([]);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(true);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MIN_LOADER_MS = 200;

  const handleCancelSearch = () => {
    searchAbortControllerRef.current?.abort();
    if (loaderTimeoutRef.current != null) {
      clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = null;
    }
    setShowLoader(false);
  };

  useEffect(() => {
    return () => {
      if (loaderTimeoutRef.current != null) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    };
  }, []);

  async function checkCanEdit() {
    if (values?.recallId) {
      const result = await httpGet(`api/recalls/canEdit?id=${values.recallId}`)
      if (result.status !== HttpStatusCode.Ok || !result.data?.canEdit) {
        window.history.back()
        return false
      }
    }
    return true
  }

  async function getData(freshLoad: boolean) {
    if (!freshLoad) return;

    handleCancelSearch();
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;
    setData(undefined);

    const startedAt = Date.now();
    setShowLoader(true);

    const currentFilters = filtersRef.current;
    setSubmittedCode(currentFilters.find(x => x.propertyName === "Code")?.value)
    setSubmittedYear(currentFilters.find(x => x.propertyName === "Year")?.value)

    try {
      const result = await httpPost(
        "api/recalls/inventorySearch",
        {
          filters: currentFilters,
          accountId: values!["accountId"],
          isDigital: values!["isDigital"]
        },
        undefined,
        undefined,
        undefined,
        { signal: abortController.signal }
      )

      if (abortController.signal.aborted) return;

      if (result.data) {
        setSubmittedParsedCode(result.data.parsedCode);
        setData(result.data.searchResults);
      }
    } catch (err: any) {
      const isAborted = abortController.signal.aborted ||
        err?.name === 'Canceled' ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED';
      if (isAborted) return;
      throw err;
    } finally {
      const elapsed = Date.now() - startedAt;
      const minRemaining = MIN_LOADER_MS - elapsed;
      if (abortController.signal.aborted) {
        setShowLoader(false);
      } else if (minRemaining > 0) {
        loaderTimeoutRef.current = setTimeout(() => {
          loaderTimeoutRef.current = null;
          setShowLoader(false);
        }, minRemaining);
      } else {
        setShowLoader(false);
      }
    }
  }

  function isSearchDisabled() {
    if (showLoader) return true

    const caseNumberFilter = filters.find(x => x.propertyName === "Code")
    if (!caseNumberFilter || !caseNumberFilter.value) return true

    const typeFilter = filters.find(x => x.propertyName === "CaseMaterialTypeId")
    if (!typeFilter || !typeFilter.value) return true

    const yearFilter = filters.find(x => x.propertyName === "Year")
    if (!yearFilter || !yearFilter.value) return true

    return false
  }

  const save = async (values: any) => {
    const canEdit = await checkCanEdit();
    if (!canEdit) return;
    let result = await httpPost("api/recallAutoSaves/update", { data: values })
    if (result.status === HttpStatusCode.Ok) {
      if (!result.data.invalidRefs || result.data.invalidRefs.length === 0)
        handleChange!("inventory")(result.data.recall.inventory)
      return result.data.invalidRefs
    };
  }

  const handleSelectInventoryButton = (inventoryItem: any) => modalSignal.value = <AddToRecallModal item={inventoryItem} addFunc={addRecallItem} />

  const handleAddToRecallButton = (inventoryItem: any) => {
    const existingInventoryItem = values!.inventory?.find((x: any) => x.caseId === inventoryItem.caseId)
    if (existingInventoryItem) {
      const index = values!.inventory.indexOf(existingInventoryItem)
      editRecallItem(
        {
          ...existingInventoryItem,
          partCases: existingInventoryItem.partCases.concat(
            [[{ specimenId: inventoryItem.specimenId, reference: inventoryItem.partCaseReference } as IPartCase]])
        },
        index
      )
    } else {
      addRecallItem({
        ...inventoryItem,
        reference: inventoryItem.caseCode ?? inventoryItem.reference,
        intent: Intent.PartCase,
        partCases: [[{ specimenId: inventoryItem.specimenId, reference: inventoryItem.partCaseReference } as IPartCase]]
      } as IRecallItem)
    }
  }

  const addRecallItem = (item: IRecallItem) => {
    const copiedValues = { ...values }
    copiedValues["inventory"] = copiedValues["inventory"] ? copiedValues["inventory"].concat(item) : [item]
    return save(copiedValues)
  }

  const openEditModal = (recallItem: IRecallItem, index: number) => modalSignal.value = <AddToRecallModal item={recallItem} addFunc={(item: IRecallItem) => editRecallItem(item, index)} />
  const editRecallItem = (item: IRecallItem, index: number) => {
    const copiedValues = { ...values }
    copiedValues["inventory"][index] = item
    return save(copiedValues)
  }

  const openRemoveModal = (index: number) => modalSignal.value = <ConfirmModal heading='Remove inventory from recall?' information='' ContinueFunc={() => remove(index)} type={ConfirmType.Destructive} />
  const remove = (index: number) => {
    const copiedValues = { ...values }
    copiedValues["inventory"].splice(index, 1)
    save(copiedValues)
  }

  const openRemoveAllModal = () => modalSignal.value = <ConfirmModal heading='Remove all inventory from recall?' information='' ContinueFunc={removeAll} type={ConfirmType.Destructive} />
  const removeAll = () => {
    const copiedValues = { ...values }
    copiedValues["inventory"] = []
    save(copiedValues)
  }

  function MapAssumedInventoryItem(item: any) {
    return {
      ...item,
      reference: getAssumedItemReference(SubmittedCode, SubmittedParsedCode),
      year: SubmittedYear
    }
  }

  useEffect(() => {
    const recallId = values?.recallId;
    const accountId = values?.accountId;
    
    if (!recallId || !accountId) {
      setLoadingFilters(false);
      return;
    }
    
    async function init() {
      // Double-check both values are defined before making API calls
      if (!recallId || !accountId) {
        setLoadingFilters(false);
        return;
      }
      
      const canEdit = await checkCanEdit();
      if (!canEdit) {
        setLoadingFilters(false);
        return;
      }
      
      setLoadingFilters(true);
      
      try {
        const [prefixesResponse, materialTypesResponse, yearsResponse] = await Promise.all([
          httpGet(`api/${EntityNames.prefixes}/filterData?accountId=${accountId}`),
          httpGet(`api/recalls/${EntityNames.caseMaterialTypes}?id=${recallId}`),
          httpGet(`api/accounts/getAccountYears?accountId=${accountId}`)
        ]);

        if (prefixesResponse.status === HttpStatusCode.Ok && prefixesResponse.data) {
          setPrefixes(prefixesResponse.data[EntityNames.prefixes] || []);
        }
        
        if (materialTypesResponse.status === HttpStatusCode.Ok && materialTypesResponse.data) {
          setMaterialTypes(materialTypesResponse.data[EntityNames.caseMaterialTypes] || []);
        }
        
        if (yearsResponse.status === HttpStatusCode.Ok && yearsResponse.data) {
          const results = (yearsResponse.data.years || []).map((year: any) => ({
            value: year.value.toString(),
            text: year.text
          }));
          setYears(results);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      } finally {
        setLoadingFilters(false);
      }
    }
    
    init();
  }, [values?.recallId, values?.accountId])

  useEffect(() => {
    if (values) {
      staticSidePanelSignal.value = <SidePanelRecallInventory
        data={values as IRecallData}
        functions={
          {
            edit: openEditModal,
            remove: openRemoveModal,
            removeAll: openRemoveAllModal,
            nextStep: nextStep!,
            prevStep: prevStep!
          }
        }
      />
    }
  }, [values])

  return (
    <>
      <div className='recall-wizard__filters-wrapper'>
        <div className='grid-x grid-margin-x grid-margin-y'>
          {loadingFilters ? (
            <div className="cell padding--xxxs" style={{ height: '200px', position: 'relative' }}>
              <div className="loaders loaders__full"></div>
            </div>
          ) : (
            <>
              <FilterTextSearchWrapper label={filterData.caseNumber.displayName} className={"small-6"} filterValues={filters} labelVisible={true} icon={undefined} EnterKeyPress={() => { }} propertyName={filterData.caseNumber.PropertyName} filter={filterData.caseNumber} onChange={(x: any) => onFiltersChange(x, filterData.caseNumber)} />
              <CaseMaterialTypesDropdown label={filterData.type.displayName} className={"small-6"} filterValues={filters} filter={filterData.type} labelVisible={true} onChange={(x: any) => onFiltersChange(x, filterData.type)} preBuiltOptions={materialTypes} />
              <FilterSelectWrapper label={filterData.year.displayName} className={"small-6"} filterValues={filters} filter={filterData.year} labelVisible={true} onChange={(x: any) => onFiltersChange(x, filterData.year)} valueCanBeNull={true} preBuiltOptions={years} />
              <FilterSelectWrapper label={filterData.prefix.displayName} className={"small-6"} filterSignal={undefined} filterValues={filters} labelVisible={true} icon={undefined} EnterKeyPress={() => { }} propertyName={filterData.prefix.PropertyName} filter={filterData.prefix} onChange={(x: any) => onFiltersChange(x, filterData.prefix)} preBuiltOptions={prefixes} />
              <div className='cell display--flex margin__top--s justifyContent--flex-end gap--s'>
                <Button
                  className=" button button--large button--secondary button--with-icon"
                  onPress={() => {
                    handleCancelSearch();
                    clearFilters();
                    setData(undefined);
                    setSubmittedCode(undefined);
                    setSubmittedYear(undefined);
                    setSubmittedParsedCode(undefined);
                  }}>
                  <p className="button-text">Clear</p>
                </Button>
                <LoadingButton
                  isLoading={showLoader}
                  buttonText='Search'
                  className=" button button--large button--primary button--with-icon"
                  isDisabled={isSearchDisabled()}
                  onPress={() => { getData(true) }}
                  loadingText="Searching"
                  spinnerColour="white">
                </LoadingButton>
              </div>
            </>
          )}
        </div>
      </div>
      <div className='recall-wizard__list-wrapper'>
        <div className="list__wrapper margin__top--s">
          <div className="list" data-testid="list-wrapper">
            {data !== undefined && data.length > 0 && renderListTable()}
            {!data && renderEmptyList("Begin by searching")}
            {data && data.length === 0 && renderEmptyList("No results")}
          </div>
        </div>
      </div>
    </>
  );

  function renderListTable() {
    return (
      <>
        <TableVirtuoso
          className="react-aria-Table"
          endReached={() => {
            getData(false)
          }}
          style={{}}
          data={data}
          initialItemCount={data!.length < pagesize ? data?.length : undefined}
          fixedHeaderContent={() => (<tr className="">
            <th><p className="small">{"Match Type"}</p></th>
            <th><p className="small">{"Type"}</p></th>
            <th><p className="small">{"Case number"}</p></th>
            <th><p className="small">{"Notes"}</p></th>
            <th><p className="small">{"Status"}</p></th>
            <th><p className="textAlign--right">{data ? `${data.length} matches found ` : undefined}</p></th>
          </tr>)}
          itemContent={(index, item) => (
            <>
              <td data-disabled={!item.canBeRecalled}>{pascalToReadable(item.resultType)} </td>
              <td data-disabled={!item.canBeRecalled}>{item.materialType.name} </td>
              <td data-disabled={!item.canBeRecalled}>{item.reference} </td>
              <td data-disabled={!item.canBeRecalled}>
                <NotesTooltip item={item} />
              </td>
              <td data-disabled={!item.canBeRecalled}><Pill statusDto={item.status} /></td>
              <td data-disabled={!item.canBeRecalled}>
                {item.canBeRecalled && <Step2ListButton inventoryItem={item.resultType === ResultType.Range ? MapAssumedInventoryItem(item) : item}
                  currentInventory={values!.inventory}
                  handleAddToRecallButton={handleAddToRecallButton}
                  handleSelectInventoryButton={handleSelectInventoryButton} />}
              </td>
            </>
          )}
        />
      </>
    )
  }

  function renderEmptyList(displayText: string) {
    return (
      <div>
        <div className="react-aria-Table">
          <table >
            <th><p className="small">{"Match Type"}</p></th>
            <th><p className="small">{"Type"}</p></th>
            <th><p className="small">{"Case number"}</p></th>
            <th><p className="small">{"Notes"}</p></th>
            <th><p className="small">{"Status"}</p></th>
            <th></th>
            {showLoader && [...Array(6)].map((e, i) => (
              <tr>
                <td> <div className="loaders loaders__list"></div> </td>
                <td> <div className="loaders loaders__list"></div> </td>
                <td> <div className="loaders loaders__list"></div> </td>
                <td> <div className="loaders loaders__list"></div> </td>
                <td> <div className="loaders loaders__list"></div> </td>
                <td></td>
              </tr>))}
          </table>
        </div>
        {!showLoader && <div className="list--empty">
          <div className="display--flex flex"><img alt='' src={IconSearch} /></div>
          <p>{displayText}</p>
        </div>}
      </div>
    )
  }
}

RecallStep2.validate = (values: Record<string, any>) => {
  const errors: Record<string, string> = {};

  return errors;
};

export default RecallStep2;